import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '@/features/auth/AuthContext'
import { supabase } from '@/lib/supabaseClient'

export default function OnboardingWelcome() {
  const { user, profile } = useAuth()
  const [showModal, setShowModal] = useState(false)
  const [step, setStep] = useState('intent')
  const [buyerIntent, setBuyerIntent] = useState(null)
  const [buyerCategory, setBuyerCategory] = useState(null)
  const [isSaving, setIsSaving] = useState(false)

  useEffect(() => {
    if (user && profile && !profile.onboarding_completed) {
      setShowModal(true)
      if (profile.role === 'buyer') {
        setStep('intent')
      } else {
        setStep('welcome')
      }
    }
  }, [user, profile])

  const handleSavePreferences = async () => {
    if (!user) return

    setIsSaving(true)
    try {
      const updates = {
        onboarding_completed: true,
      }

      if (buyerIntent) {
        updates.buyer_intent = buyerIntent
      }

      if (buyerCategory) {
        updates.buyer_category = buyerCategory
      }

      const { error } = await supabase
        .from('profiles')
        .update(updates)
        .eq('id', user.id)

      if (error) throw error

      handleClose()
    } catch (error) {
      console.error('Error saving preferences:', error)
    } finally {
      setIsSaving(false)
    }
  }

  const handleSkip = async () => {
    if (!user) return

    try {
      await supabase
        .from('profiles')
        .update({ onboarding_completed: true })
        .eq('id', user.id)

      handleClose()
    } catch (error) {
      console.error('Error skipping onboarding:', error)
    }
  }

  const handleClose = () => {
    setShowModal(false)
  }

  if (!showModal || !profile) return null

  const steps = {
    buyer: [
      {
        icon: '🔍',
        title: 'Browse Projects',
        description: 'Explore off-plan developments from verified developers across New Zealand',
        link: '/projects',
        linkText: 'View Projects',
      },
      {
        icon: '⭐',
        title: 'Follow Favorites',
        description: 'Follow projects you\'re interested in to receive updates and notifications',
      },
      {
        icon: '🏠',
        title: 'Reserve Units',
        description: 'Reserve your dream unit directly with developers and track your reservations',
        link: '/dashboard/buyer',
        linkText: 'My Dashboard',
      },
    ],
    developer: [
      {
        icon: '➕',
        title: 'Create Projects',
        description: 'List your off-plan developments and reach thousands of potential buyers',
        link: '/developer/projects/create',
        linkText: 'Create Project',
      },
      {
        icon: '📊',
        title: 'Manage Units',
        description: 'Add units, track reservations, and manage your project portfolio',
        link: '/dashboard/developer',
        linkText: 'My Dashboard',
      },
      {
        icon: '💬',
        title: 'Connect with Buyers',
        description: 'Receive reservations and communicate directly with interested buyers',
      },
    ],
    admin: [
      {
        icon: '✅',
        title: 'Review Projects',
        description: 'Review and approve new project submissions from developers',
        link: '/dashboard/admin',
        linkText: 'Admin Dashboard',
      },
      {
        icon: '📈',
        title: 'Monitor Platform',
        description: 'Track analytics, user activity, and platform health',
      },
      {
        icon: '🔧',
        title: 'Manage Users',
        description: 'Manage users, handle disputes, and maintain platform quality',
      },
    ],
  }

  const userSteps = steps[profile.role] || []
  const isBuyer = profile.role === 'buyer'

  const renderBuyerQuestionnaire = () => {
    if (step === 'intent') {
      return (
        <>
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-primary-600 rounded-full flex items-center justify-center text-white text-3xl mx-auto mb-4">
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-secondary-900 mb-2">
              Are you buying your first home or investing?
            </h2>
            <p className="text-secondary-600">
              We'll personalize your experience based on your answer
            </p>
          </div>

          <div className="space-y-4 mb-8">
            <button
              onClick={() => {
                setBuyerIntent('owner_occupied')
                setStep('category')
              }}
              className="w-full p-5 bg-secondary-50 hover:bg-primary-50 border-2 border-transparent hover:border-primary-600 rounded-lg text-left transition-all group"
            >
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-primary-100 group-hover:bg-primary-200 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors">
                  <svg className="w-6 h-6 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-secondary-900 mb-1">
                    Buying to live in
                  </h3>
                  <p className="text-secondary-600 text-sm">
                    I'm looking for my home
                  </p>
                  <p className="text-xs text-secondary-500 mt-2">
                    We'll focus on deposit assistance and home buyer incentives
                  </p>
                </div>
              </div>
            </button>

            <button
              onClick={() => {
                setBuyerIntent('investment')
                setBuyerCategory('investor')
                handleSavePreferences()
              }}
              disabled={isSaving}
              className="w-full p-5 bg-secondary-50 hover:bg-primary-50 border-2 border-transparent hover:border-primary-600 rounded-lg text-left transition-all group"
            >
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-green-100 group-hover:bg-green-200 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors">
                  <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-secondary-900 mb-1">
                    Investing
                  </h3>
                  <p className="text-secondary-600 text-sm">
                    I'm buying as an investment property
                  </p>
                  <p className="text-xs text-secondary-500 mt-2">
                    We'll show yield calculations and maximize your price reduction
                  </p>
                </div>
              </div>
            </button>
          </div>

          <button
            onClick={handleSkip}
            className="w-full text-secondary-500 hover:text-secondary-700 py-2 text-sm"
          >
            I'll decide later
          </button>
        </>
      )
    }

    if (step === 'category') {
      return (
        <>
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-primary-600 rounded-full flex items-center justify-center text-white mx-auto mb-4">
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-secondary-900 mb-2">
              Which best describes you?
            </h2>
            <p className="text-secondary-600">
              This may qualify you for special incentives
            </p>
          </div>

          <div className="space-y-3 mb-8">
            <button
              onClick={() => {
                setBuyerCategory('first_home')
                handleSavePreferences()
              }}
              disabled={isSaving}
              className="w-full p-4 bg-green-50 hover:bg-green-100 border-2 border-green-200 hover:border-green-400 rounded-lg text-left transition-all"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-green-200 rounded-lg flex items-center justify-center flex-shrink-0">
                  <svg className="w-5 h-5 text-green-700" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                  </svg>
                </div>
                <div>
                  <div className="font-semibold text-secondary-900">First Home Buyer</div>
                  <p className="text-xs text-green-700 mt-0.5">May be eligible for up to $10,000 deposit assistance</p>
                </div>
              </div>
            </button>

            <button
              onClick={() => {
                setBuyerCategory('immigrant')
                handleSavePreferences()
              }}
              disabled={isSaving}
              className="w-full p-4 bg-secondary-50 hover:bg-primary-50 border-2 border-transparent hover:border-primary-600 rounded-lg text-left transition-all"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-secondary-200 rounded-lg flex items-center justify-center flex-shrink-0">
                  <svg className="w-5 h-5 text-secondary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div className="font-semibold text-secondary-900">Recent Immigrant</div>
              </div>
            </button>

            <button
              onClick={() => {
                setBuyerCategory('upgrading')
                handleSavePreferences()
              }}
              disabled={isSaving}
              className="w-full p-4 bg-secondary-50 hover:bg-primary-50 border-2 border-transparent hover:border-primary-600 rounded-lg text-left transition-all"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-secondary-200 rounded-lg flex items-center justify-center flex-shrink-0">
                  <svg className="w-5 h-5 text-secondary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
                  </svg>
                </div>
                <div className="font-semibold text-secondary-900">Upgrading</div>
              </div>
            </button>

            <button
              onClick={() => {
                setBuyerCategory('downsizing')
                handleSavePreferences()
              }}
              disabled={isSaving}
              className="w-full p-4 bg-secondary-50 hover:bg-primary-50 border-2 border-transparent hover:border-primary-600 rounded-lg text-left transition-all"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-secondary-200 rounded-lg flex items-center justify-center flex-shrink-0">
                  <svg className="w-5 h-5 text-secondary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                  </svg>
                </div>
                <div className="font-semibold text-secondary-900">Downsizing</div>
              </div>
            </button>
          </div>

          <div className="flex gap-3">
            <button
              onClick={() => setStep('intent')}
              className="flex-1 btn-outline py-2"
              disabled={isSaving}
            >
              Back
            </button>
            <button
              onClick={handleSkip}
              className="flex-1 text-secondary-500 hover:text-secondary-700 py-2 text-sm"
              disabled={isSaving}
            >
              I'll decide later
            </button>
          </div>
        </>
      )
    }

    return null
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-8">
          {isBuyer && step !== 'welcome' ? (
            renderBuyerQuestionnaire()
          ) : (
            <>
              <div className="text-center mb-8">
                <div className="w-16 h-16 bg-primary-600 rounded-full flex items-center justify-center text-white text-3xl mx-auto mb-4">
                  👋
                </div>
                <h2 className="text-3xl font-bold text-secondary-900 mb-2">
                  Welcome to Beaver!
                </h2>
                <p className="text-lg text-secondary-600">
                  {profile.full_name || 'User'}, let's get you started
                </p>
              </div>

              <div className="space-y-6 mb-8">
                {userSteps.map((step, index) => (
                  <div key={index} className="flex gap-4 p-6 bg-secondary-50 rounded-lg">
                    <div className="text-4xl">{step.icon}</div>
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-secondary-900 mb-2">
                        {step.title}
                      </h3>
                      <p className="text-secondary-600 mb-3">{step.description}</p>
                      {step.link && (
                        <Link
                          to={step.link}
                          onClick={handleClose}
                          className="text-primary-600 hover:text-primary-700 font-medium inline-flex items-center gap-1"
                        >
                          {step.linkText}
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </Link>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex flex-col sm:flex-row gap-3">
                {isBuyer ? (
                  <>
                    <button
                      onClick={() => setStep('intent')}
                      className="flex-1 btn-primary py-3"
                    >
                      Continue
                    </button>
                    <button
                      onClick={handleSkip}
                      className="flex-1 btn-outline py-3"
                    >
                      Skip
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      onClick={handleSkip}
                      className="flex-1 btn-primary py-3"
                    >
                      Get Started
                    </button>
                    <Link
                      to="/settings"
                      onClick={handleSkip}
                      className="flex-1 btn-outline py-3 text-center"
                    >
                      Customize Settings
                    </Link>
                  </>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
